import {
  ApiCreateUpdatePersonalisationPayload,
  ApiInsertionType,
  ApiSimplePersonalisation,
} from "@incendium/api";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material";
import { personalisationService } from "Apis";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import Loading from "Components/Loading/Loading";
import LocationDropdown from "Components/Location/LocationDropdown";
import { enumToArray, formatEnumVal } from "Helpers/enumToText";
import { useNotification, useSelectedProject } from "Hooks";
import { useComponents } from "Hooks/useComponents";
import { GenericDialoagProps } from "Interfaces";
import produce from "immer";
import { useCallback, useEffect, useState } from "react";
import {
  SelectValidator,
  TextValidator,
  ValidatorForm,
} from "react-material-ui-form-validator";
import { usePrevious } from "react-use";
import { useConfirmationContext } from "Providers/ConfirmationProvider";
import { getDetailsByCode } from "Helpers/errors";

interface IPersonalisationDialogProps extends GenericDialoagProps {
  personlisation: ApiSimplePersonalisation;
  campaignId?: number;
  onSaved: (c: ApiSimplePersonalisation) => void;
}

function PersonalisationDialog({
  open,
  setOpen,
  campaignId,
  personlisation: originalPersonalisation,
  onSaved,
}: IPersonalisationDialogProps) {
  const { selectedProject } = useSelectedProject();
  const { components, loading } = useComponents();
  const [personlisation, setPersonlisation] = useState(originalPersonalisation);
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { openConfirmation, closeConfirmation } = useConfirmationContext();
  const prevOpen = usePrevious(open);

  useEffect(() => {
    if (!prevOpen && open) {
      setPersonlisation(originalPersonalisation);
    }
  }, [open, prevOpen, originalPersonalisation]);

  const onClose = useCallback(() => {
    setOpen(false);
    setPersonlisation({});
  }, [setOpen]);

  const onSubmit = useCallback(async () => {
    // Get the component name to use as the personalisation name
    const selectedComponent = components.find(
      (comp) => comp.id === personlisation.componentId
    );
    const name = selectedComponent?.name || personlisation.name || "";

    const payload: ApiCreateUpdatePersonalisationPayload = {
      ...personlisation,
      name,
      campaignId,
    };
    try {
      const fn = personlisation.id
        ? personalisationService.personalisationServiceUpdatePersonalisation({
          projectId: selectedProject?.id as number,
          personalisationId: personlisation.id,
          payload,
        })
        : personalisationService.personalisationServiceCreatePersonalisation({
          projectId: selectedProject?.id as number,
          payload,
        });

      const res = await fn;
      onSaved(res.personalisation || {});
      showSuccessNotification(`${name} Added to Campaign`);
      onClose();
    } catch (error: any) {
      const t = await error.json();
      if (t.code === 9) {
        openConfirmation({
          title: `This Personalization is used in active campaign.`,
          body: "Making changes to this Personalization might result in inaccuracies in your analytics. Do you wish to proceed?",
          callback: async () => {
            try {
              const res =
                await personalisationService.personalisationServiceUpdatePersonalisation(
                  {
                    projectId: selectedProject?.id as number,
                    personalisationId: personlisation.id as number,
                    payload,
                    token: getDetailsByCode(t.details, 9).message,
                  }
                );

              onSaved(res.personalisation || {});
              const selectedComponent = components.find(
                (comp) => comp.id === personlisation.componentId
              );
              const name = selectedComponent?.name || personlisation.name || "";
              showSuccessNotification(`${name} Saved`);
              onClose();
            } catch (error) {
              showErrorNotification(
                `Internal Error deleting Personalization, please try again`
              );
            }
            closeConfirmation();
          },
        });
        return;
      }

      showErrorNotification(
        `Internal Error saving Personalization settings, please try again`
      );
    }
  }, [
    personlisation,
    showSuccessNotification,
    showErrorNotification,
    onSaved,
    selectedProject?.id,
    onClose,
    campaignId,
    openConfirmation,
    closeConfirmation,
    components,
  ]);

  return (
    <CustomDialog onClose={onClose} fullWidth maxWidth="sm" open={open}>
      <DialogTitle>
        Configure Personalization
      </DialogTitle>
      {loading ? (
        <Loading />
      ) : components.length === 0 ? (
        <DialogContent>
          <Typography variant="subtitle2">
            It Looks like you don't have any Components created yet.
          </Typography>
        </DialogContent>
      ) : (
        <ValidatorForm onSubmit={onSubmit}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SelectValidator
                  size={"small"}
                  variant="outlined"
                  fullWidth
                  label="Component to Insert"
                  name="component"
                  value={personlisation.componentId || ""}
                  validators={["required"]}
                  errorMessages={["Component is required"]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const componentId = Number(e.target.value);
                    const selectedComponent = components.find(
                      (comp) => comp.id === componentId
                    );
                    setPersonlisation(
                      produce(personlisation, (draft) => {
                        draft.componentId = componentId;
                        // Automatically set name to component name
                        if (selectedComponent) {
                          draft.name = selectedComponent.name;
                        }
                      })
                    );
                  }}
                >
                  {(components || []).map((comp) => (
                    <MenuItem key={comp.id} value={comp.id}>
                      {comp.name}
                    </MenuItem>
                  ))}
                </SelectValidator>
                <FormHelperText>
                  Select the component you want to personalize with
                </FormHelperText>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <LocationDropdown
                  label="Location"
                  size={"small"}
                  variant="outlined"
                  fullWidth
                  name="location"
                  value={personlisation.locationId}
                  validators={["required"]}
                  errorMessages={["Location is required"]}
                  onChange={(id) => {
                    setPersonlisation(
                      produce(personlisation, (draft) => {
                        draft.locationId = id;
                      })
                    );
                  }}
                />
                <FormHelperText>
                  Where this personalization should run
                </FormHelperText>
              </Grid>

              <Grid item xs={12}>
                <TextValidator
                  variant="outlined"
                  size="small"
                  fullWidth
                  label="CSS Selector"
                  name="appliedTo"
                  placeholder="e.g., #header, .product-list, div.main-content"
                  value={personlisation.appliedTo || ""}
                  validators={["required"]}
                  errorMessages={["CSS Selector is required"]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPersonlisation(
                      produce(personlisation, (draft) => {
                        draft.appliedTo = e.target.value;
                      })
                    );
                  }}
                />
                <FormHelperText>
                  The CSS selector where the component will be inserted
                </FormHelperText>
              </Grid>

              <Grid item xs={12}>
                <SelectValidator
                  size={"small"}
                  variant="outlined"
                  fullWidth
                  label="Insertion Method"
                  name="insertionType"
                  value={personlisation.insertionType || ""}
                  validators={["required"]}
                  errorMessages={["Insertion Method is required"]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPersonlisation(
                      produce(personlisation, (draft) => {
                        draft.insertionType = e.target
                          .value as ApiInsertionType;
                      })
                    );
                  }}
                >
                  {enumToArray(ApiInsertionType)
                    .filter((ins) => ins !== ApiInsertionType.IT_UNSPECIFIED)
                    .map((ins) => (
                      <MenuItem key={ins} value={ins}>
                        {formatEnumVal(ins)}
                      </MenuItem>
                    ))}
                </SelectValidator>
                <FormHelperText>
                  How the component should be inserted relative to the target element
                </FormHelperText>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="secondary">
              Cancel
            </Button>
            <Button variant="contained" color="primary" type="submit">
              {personlisation?.id ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </ValidatorForm>
      )}
    </CustomDialog>
  );
}

export default PersonalisationDialog;
